import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Trophy,
  TrendingUp,
  AlertTriangle,
  Lightbulb,
  MessageCircle,
  Clock,
  RotateCcw,
  BarChart2,
  Play,
  Loader2,
  CheckCircle2,
  Brain,
  FileText,
  BarChart
} from 'lucide-react';
import { useConfig } from '../context/ConfigContext';
import { useAuth } from '../context/AuthContext';
import { trackEvent } from '../lib/telemetry';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import ScoreRing from '../components/coaching/ScoreRing';
import CategoryScore from '../components/coaching/CategoryScore';
import PracticeAgainButton from '../components/training/PracticeAgainButton';
import ShareButton from '../components/social/ShareButton';
import DecisionTreeVisualization from '../components/practice/DecisionTreeVisualization';
import {
  scoreWithDeadline,
  fetchWithTimeout,
  readStoredResults,
  writeStoredResults,
  initialStatusForSession,
  normalizeAnalysisStatus
} from '../lib/analysisFlow';

function Results() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { sessionId: paramSessionId } = useParams();
  const { lastResults, setLastResults, clearSession } = useConfig();
  const { authFetch } = useAuth();

  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('processing');
  const [recoveredFromStorage, setRecoveredFromStorage] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const analysisTriggeredRef = useRef(false);

  // Load historical session from ?session= param or :sessionId route param
  const historicalSessionId = searchParams.get('session') || paramSessionId;

  useEffect(() => {
    if (historicalSessionId && historicalSessionId !== lastResults?.sessionId) {
      // Refresh of a just-finished call: resume the saved attempt (its
      // scenario/module context and scoring state) instead of restarting.
      const stored = readStoredResults(window.sessionStorage, historicalSessionId);
      if (stored) {
        setLastResults(stored);
        return;
      }
      loadHistoricalSession(historicalSessionId);
    }
  }, [historicalSessionId]);

  // Keep the stored copy in step with the page, so a refresh never re-enters a
  // stale 'processing' state that was really a failure (feedback 7bc75409).
  useEffect(() => {
    if (lastResults?.sessionId) writeStoredResults(window.sessionStorage, lastResults);
  }, [lastResults]);

  // Usage telemetry: one results_viewed per session shown on this page.
  const viewedSessionRef = useRef(null);
  useEffect(() => {
    const sessionId = lastResults?.sessionId;
    if (!sessionId || viewedSessionRef.current === sessionId) return;
    viewedSessionRef.current = sessionId;
    trackEvent('results_viewed');
  }, [lastResults?.sessionId]);

  // Usage telemetry: scoring outcomes count only while this page is open, so a
  // score that lands after the rep has left is not recorded as shown.
  const pageOpenRef = useRef(false);
  useEffect(() => {
    pageOpenRef.current = true;
    return () => { pageOpenRef.current = false; };
  }, []);
  const trackScoring = (event, options) => {
    if (pageOpenRef.current) trackEvent(event, options);
  };

  const loadHistoricalSession = async (sessionId) => {
    setLoadingHistory(true);
    try {
      const response = await authFetch(`/api/training/session/${sessionId}`);
      if (!response.ok) throw new Error('Session not found');

      const data = await response.json();
      const session = data.session;

      if (session) {
        const hasAnalysis = session.overall_score !== null && session.overall_score !== undefined;
        setLastResults({
          sessionId: session.id,
          analysis: hasAnalysis ? {
            overallScore: session.overall_score,
            categories: session.category_scores,
            strengths: session.strengths,
            improvements: session.improvements,
            summary: session.category_scores
              ? `Score: ${session.overall_score}/100 across ${Object.keys(session.category_scores).length} categories.`
              : null,
            keyMoment: session.key_moment || null,
            nextSteps: (Array.isArray(session.next_steps) && session.next_steps.length > 0)
              ? session.next_steps
              : session.improvements?.map(i => i.alternative).filter(Boolean)?.slice(0, 3) || []
          } : null,
          scenario: {
            id: session.scenario_id,
            name: session.scenario_name
          },
          duration: session.duration_seconds,
          // An unscored attempt with a real transcript resumes scoring here
          // instead of sending the trainee back to redo the call.
          analysisStatus: initialStatusForSession(session),
          transcript: {
            raw: session.transcript_raw,
            formatted: session.transcript_formatted
          }
        });
      }
    } catch (err) {
      console.error('Error loading historical session:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Try to recover results from sessionStorage if not in context
  useEffect(() => {
    if (!lastResults && !recoveredFromStorage && !historicalSessionId) {
      setRecoveredFromStorage(true);
      try {
        const stored = readStoredResults(window.sessionStorage);
        if (stored) {
          setLastResults(stored);
        }
      } catch (e) {
        console.error('Error recovering from sessionStorage:', e);
      }
    }
  }, [lastResults, recoveredFromStorage, setLastResults, historicalSessionId]);

  // Save session results to database
  const saveSessionResults = useCallback(async (analysis) => {
    const sessionId = lastResults?.sessionId;
    console.log('[SESSION SAVE] Attempting to save, sessionId:', sessionId);
    if (!sessionId) {
      console.log('[SESSION SAVE] No session ID, skipping database save');
      return;
    }

    try {
      const response = await authFetch(`/api/training/session/${sessionId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          transcript_raw: lastResults?.transcript?.raw || '',
          transcript_formatted: lastResults?.transcript?.formatted || [],
          overall_score: analysis.overallScore,
          category_scores: analysis.categories,
          strengths: analysis.strengths,
          improvements: analysis.improvements,
          duration_seconds: lastResults?.duration || 0
        })
      });

      if (response.ok) {
        console.log('[SESSION SAVE] Success - session saved to database');

        // Also log this as practice for today's practice tracking
        try {
          await authFetch('/api/practice/log', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              duration_seconds: lastResults?.duration || 0,
              session_id: sessionId
            })
          });
          console.log('[PRACTICE LOG] Practice logged successfully');
        } catch (practiceErr) {
          console.error('[PRACTICE LOG] Error logging practice:', practiceErr);
        }
      } else {
        const errorText = await response.text();
        console.error('[SESSION SAVE] Failed:', response.status, errorText);
      }
    } catch (err) {
      console.error('[SESSION SAVE] Error:', err);
    }
  }, [lastResults?.sessionId, lastResults?.transcript, lastResults?.duration, authFetch]);

  // Run the analysis. The /api/analysis/queue endpoint runs analysis
  // synchronously server-side (Vercel maxDuration 300s) and returns the result.
  // The wait is BOUNDED (per-request timeout + overall deadline); a failure
  // lands on a 'failed' state with a retry that re-scores the SAME session.
  // Module credit is granted on the server when the call is scored, so it no
  // longer depends on this page staying open (feedback 7bc75409).
  const triggerAnalysis = useCallback(async () => {
    const sessionId = lastResults?.sessionId;
    if (!sessionId || analysisTriggeredRef.current) return;
    analysisTriggeredRef.current = true;

    const applyAnalysis = (analysisData) => {
      setLastResults(prev => ({ ...prev, analysis: analysisData, analysisStatus: 'completed', analysisError: null }));
      trackScoring('score_shown');
    };

    const normalize = (raw) => raw && (raw.categories || raw.overallScore !== undefined) ? raw : (raw ? {
      overallScore: raw.overall_score,
      categories: raw.category_scores,
      summary: raw.summary,
      strengths: raw.strengths,
      improvements: raw.improvements,
      keyMoment: raw.key_moment,
      nextSteps: raw.next_steps
    } : null);

    const requestOnce = async () => {
      const response = await fetchWithTimeout(authFetch, '/api/analysis/queue', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId })
      });
      const data = await response.json().catch(() => ({}));
      return { ok: response.ok, status: response.status, data };
    };

    const result = await scoreWithDeadline(requestOnce);

    if (result.outcome === 'completed') {
      applyAnalysis(normalize(result.analysis));
      return;
    }

    if (result.outcome === 'tooShort') {
      setLastResults(prev => ({ ...prev, analysisStatus: 'tooShort' }));
      trackScoring('scoring_failed', { detail: 'too_short' });
      return;
    }

    // Last check: the server may have saved a score even though our request
    // failed (e.g. the response was lost mid-flight).
    try {
      const resultsResponse = await fetchWithTimeout(authFetch, `/api/analysis/results/${sessionId}`, {}, 15_000);
      if (resultsResponse.ok) {
        const resultsData = await resultsResponse.json();
        if (resultsData.analysis) {
          applyAnalysis(normalize(resultsData.analysis));
          return;
        }
      }
    } catch { /* fall through to the failed state */ }

    console.error('[Analysis] Scoring failed:', result.errorType, result.message);
    trackScoring('scoring_failed', {
      detail: result.errorType === 'timeout' ? 'timed_out' : result.errorType === 'network' ? 'network' : 'server'
    });
    setLastResults(prev => ({
      ...prev,
      analysisStatus: 'failed',
      analysisError: result.message,
      analysisRetryable: result.retryable !== false
    }));
  }, [lastResults?.sessionId, authFetch, setLastResults]);

  // Retry scoring for the SAME attempt — never restarts the call or module.
  const retryAnalysis = useCallback(() => {
    trackEvent('scoring_retried');
    analysisTriggeredRef.current = false;
    setRetrying(true);
    setAnalysisProgress(0);
    setLastResults(prev => ({ ...prev, analysisStatus: 'processing', analysisError: null }));
  }, [setLastResults]);

  useEffect(() => {
    if (retrying && lastResults?.analysisStatus !== 'processing') setRetrying(false);
  }, [retrying, lastResults?.analysisStatus]);

  // Kick off analysis once when results land in the 'processing' state.
  useEffect(() => {
    if (lastResults?.analysisStatus === 'processing' && lastResults?.sessionId && !lastResults?.analysis) {
      triggerAnalysis();

      // Animate progress bar + steps while we wait for the server.
      const steps = ['processing', 'analyzing', 'scoring', 'generating'];
      let stepIndex = 0;
      const stepInterval = setInterval(() => {
        stepIndex = (stepIndex + 1) % steps.length;
        setCurrentStep(steps[stepIndex]);
        setAnalysisProgress(prev => Math.min(95, prev + 8));
      }, 3000);

      return () => clearInterval(stepInterval);
    }
  }, [lastResults?.analysisStatus, lastResults?.sessionId, lastResults?.analysis, triggerAnalysis]);

  // Redirect if no results (but wait for recovery attempt and historical load)
  useEffect(() => {
    if (!lastResults && recoveredFromStorage && !historicalSessionId && !loadingHistory) {
      navigate('/');
    }
  }, [lastResults, recoveredFromStorage, historicalSessionId, loadingHistory, navigate]);

  // Helper function - defined early since it's used in loading states
  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Show loading while trying to recover or load historical session
  if (!lastResults) {
    if (!recoveredFromStorage || loadingHistory || historicalSessionId) {
      return (
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <Loader2 className="w-12 h-12 text-primary-400 mx-auto animate-spin" />
          <p className="text-muted-foreground mt-4">Loading results...</p>
        </div>
      );
    }
    return null;
  }

  const { analysis, scenario, duration } = lastResults;
  const analysisStatus = normalizeAnalysisStatus(lastResults.analysisStatus);

  const isModuleScenario = scenario?.isGeneratedScenario && scenario?.moduleId;

  // Show loading state while analysis is in progress
  if (analysisStatus === 'processing') {
    const steps = [
      { id: 'processing', label: 'Processing transcript', icon: FileText },
      { id: 'analyzing', label: 'Analyzing conversation', icon: Brain },
      { id: 'scoring', label: 'Calculating scores', icon: BarChart },
      { id: 'generating', label: 'Generating feedback', icon: Lightbulb }
    ];

    return (
      <div className="max-w-2xl mx-auto px-4 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          {/* Animated loader */}
          <div className="relative w-32 h-32 mx-auto mb-8">
            <motion.div
              className="absolute inset-0 rounded-full border-4 border-primary-500/20"
            />
            <motion.div
              className="absolute inset-0 rounded-full border-4 border-transparent border-t-primary-500"
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <Brain className="w-12 h-12 text-primary-400" />
            </div>
          </div>

          <h2 className="text-2xl font-bold text-foreground mb-2">
            Analyzing Your Performance
          </h2>
          <p className="text-muted-foreground mb-8">
            Our AI coach is reviewing your conversation and preparing detailed feedback
          </p>

          {/* Progress bar */}
          <div className="w-full bg-muted rounded-full h-2 mb-6 overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-primary-500 to-purple-500"
              initial={{ width: 0 }}
              animate={{ width: `${analysisProgress}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>

          {/* Progress steps */}
          <div className="grid grid-cols-2 gap-4 text-left">
            {steps.map((step, index) => {
              const StepIcon = step.icon;
              const isActive = currentStep === step.id;
              const isPast = steps.findIndex(s => s.id === currentStep) > index;

              return (
                <motion.div
                  key={step.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                    isActive ? 'bg-primary-500/10 border border-primary-500/30' :
                    isPast ? 'bg-green-500/10' : 'bg-card'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    isPast ? 'bg-green-500/20' : isActive ? 'bg-primary-500/20' : 'bg-muted'
                  }`}>
                    {isPast ? (
                      <CheckCircle2 className="w-4 h-4 text-green-400" />
                    ) : isActive ? (
                      <Loader2 className="w-4 h-4 text-primary-400 animate-spin" />
                    ) : (
                      <StepIcon className="w-4 h-4 text-muted-foreground" />
                    )}
                  </div>
                  <span className={`text-sm ${
                    isPast ? 'text-green-400' : isActive ? 'text-primary-400' : 'text-muted-foreground'
                  }`}>
                    {step.label}
                  </span>
                </motion.div>
              );
            })}
          </div>

          {/* Call summary while waiting */}
          <div className="mt-8 p-4 bg-card rounded-lg border border-border">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Scenario</span>
              <span className="text-foreground">{scenario?.name}</span>
            </div>
            <div className="flex items-center justify-between text-sm mt-2">
              <span className="text-muted-foreground">Call Duration</span>
              <span className="text-foreground">{formatDuration(duration)}</span>
            </div>
            <div className="flex items-center justify-between text-sm mt-2">
              <span className="text-muted-foreground">Customer</span>
              <span className="text-foreground">{scenario?.customerName}</span>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // Clear sessionStorage when leaving results
  const cleanupAndNavigate = (path) => {
    try {
      sessionStorage.removeItem('lastTrainingResults');
    } catch (e) {
      // Ignore
    }
    clearSession();
    navigate(path);
  };

  const handleTryAgain = () => {
    trackEvent('results_next_step', { detail: 'same_scenario' });
    if (!scenario?.id) {
      cleanupAndNavigate('/');
      return;
    }
    if (isModuleScenario) {
      cleanupAndNavigate(`/modules/${scenario.moduleId}`);
    } else {
      cleanupAndNavigate(`/scenario/${scenario.id}`);
    }
  };

  const handleNewScenario = () => {
    trackEvent('results_next_step', { detail: 'new_scenario' });
    if (isModuleScenario && scenario?.moduleId) {
      cleanupAndNavigate(`/modules/${scenario.moduleId}`);
    } else {
      cleanupAndNavigate('/');
    }
  };

  const handleBackToModule = () => {
    trackEvent('results_next_step', { detail: 'back_to_module' });
    if (scenario?.moduleId) {
      cleanupAndNavigate(`/modules/${scenario.moduleId}`);
    } else {
      cleanupAndNavigate('/');
    }
  };

  // Call captured too little speech to analyze (e.g. a short test call or a
  // dropped connection). Be honest — refreshing won't produce a score.
  if (analysisStatus === 'tooShort') {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 text-center">
        <MessageCircle className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-foreground mb-4">Call Too Short to Score</h2>
        <p className="text-muted-foreground mb-6">
          This call didn&apos;t capture enough conversation to analyze. Run through a full
          scenario — speak with the customer for at least a minute — and you&apos;ll get a complete scorecard.
        </p>
        <div className="flex gap-4 justify-center">
          <Button variant="secondary" onClick={handleNewScenario}>
            Try Another Scenario
          </Button>
          <Button onClick={handleTryAgain}>
            Retry This Scenario
          </Button>
        </div>
      </div>
    );
  }

  // Scoring failed (or ran past the deadline). The call itself is saved: offer
  // a retry that re-scores this same attempt instead of redoing the call.
  if (analysisStatus === 'failed') {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 text-center">
        <AlertTriangle className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-foreground mb-4">We Couldn&apos;t Score This Call Yet</h2>
        <p className="text-muted-foreground mb-2">
          Your call was saved. You don&apos;t need to redo it.
        </p>
        <p className="text-muted-foreground mb-6">
          {lastResults.analysisError || 'Scoring failed. Retry scoring in a minute.'}
        </p>
        <div className="flex gap-4 justify-center">
          <Button variant="secondary" onClick={isModuleScenario ? handleBackToModule : handleNewScenario}>
            {isModuleScenario ? 'Back to Module' : 'Try Another Scenario'}
          </Button>
          {lastResults.analysisRetryable !== false && (
            <Button onClick={retryAnalysis} disabled={retrying}>
              <RotateCcw className="w-4 h-4 mr-2" />
              Retry Scoring
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Handle case where analysis failed
  if (!analysis || analysis.parseError) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 text-center">
        <AlertTriangle className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-foreground mb-4">Analysis Unavailable</h2>
        <p className="text-muted-foreground mb-6">
          We couldn't analyze your call this time. Your conversation was still valuable practice.
        </p>
        <div className="flex gap-4 justify-center">
          <Button variant="secondary" onClick={handleNewScenario}>
            Try Another Scenario
          </Button>
          <Button onClick={handleTryAgain}>
            Retry This Scenario
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Back Button */}
      <motion.button
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        onClick={handleNewScenario}
        className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to scenarios
      </motion.button>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-8"
      >
        <h1 className="text-3xl font-bold text-foreground mb-2">Training Complete</h1>
        <p className="text-muted-foreground">{scenario.name}</p>
        <div className="flex items-center justify-center gap-4 mt-4 text-sm text-muted-foreground">
          <span className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            {formatDuration(duration)}
          </span>
        </div>
      </motion.div>

      {/* Overall Score */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mb-8"
      >
        <Card className="text-center py-8 relative">
          {/* Share Button for high scores */}
          {analysis.overallScore >= 80 && (
            <div className="absolute top-4 right-4">
              <ShareButton
                achievementType="score"
                achievementData={{
                  score: analysis.overallScore,
                  scenarioId: scenario.id,
                  scenarioName: scenario.name
                }}
                variant="icon"
              />
            </div>
          )}
          <div className="mb-4">
            <ScoreRing score={analysis.overallScore} size={160} />
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-2">
            {getScoreLabel(analysis.overallScore)}
          </h2>
          <p className="text-muted-foreground max-w-lg mx-auto">
            {analysis.summary}
          </p>
        </Card>
      </motion.div>

      {/* Category Scores */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="mb-8"
      >
        <h3 className="text-lg font-semibold text-foreground mb-4">Performance Breakdown</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {analysis.categories && Object.entries(analysis.categories).map(([key, data], index) => (
            <CategoryScore
              key={key}
              name={formatCategoryName(key)}
              score={data.score}
              feedback={data.feedback}
              delay={index * 0.05}
            />
          ))}
        </div>
      </motion.div>

      {/* Strengths */}
      {analysis.strengths && analysis.strengths.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-8"
        >
          <Card>
            <Card.Header>
              <div className="flex items-center gap-2">
                <Trophy className="w-5 h-5 text-green-400" />
                <Card.Title>What You Did Well</Card.Title>
              </div>
            </Card.Header>
            <Card.Content>
              <div className="space-y-4">
                {analysis.strengths.map((strength, index) => (
                  <div key={index} className="p-4 bg-green-500/10 rounded-lg border border-green-500/20">
                    <h4 className="font-medium text-green-400 mb-1">{strength.title}</h4>
                    <p className="text-sm text-secondary-foreground mb-2">{strength.description}</p>
                    {strength.quote && (
                      <p className="text-sm text-muted-foreground italic">"{strength.quote}"</p>
                    )}
                  </div>
                ))}
              </div>
            </Card.Content>
          </Card>
        </motion.div>
      )}

      {/* Improvements */}
      {analysis.improvements && analysis.improvements.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-8"
        >
          <Card>
            <Card.Header>
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-yellow-400" />
                <Card.Title>Areas to Improve</Card.Title>
              </div>
            </Card.Header>
            <Card.Content>
              <div className="space-y-4">
                {analysis.improvements.map((improvement, index) => (
                  <div key={index} className="p-4 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
                    <h4 className="font-medium text-yellow-400 mb-1">{improvement.title}</h4>
                    <p className="text-sm text-secondary-foreground mb-2">{improvement.issue}</p>
                    {improvement.quote && (
                      <div className="mb-2">
                        <span className="text-xs text-muted-foreground">You said:</span>
                        <p className="text-sm text-muted-foreground italic">"{improvement.quote}"</p>
                      </div>
                    )}
                    {improvement.alternative && (
                      <div className="mt-3 p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
                        <span className="text-xs font-medium text-blue-700 dark:text-blue-300 flex items-center gap-1 mb-1">
                          <Lightbulb className="w-3 h-3" /> Try instead:
                        </span>
                        <p className="text-sm text-blue-900 dark:text-blue-100">"{improvement.alternative}"</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Card.Content>
          </Card>
        </motion.div>
      )}

      {/* Decision Tree Visualization */}
      {lastResults?.branchingPoints && lastResults?.branchPath && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="mb-8"
        >
          <DecisionTreeVisualization
            branchingPoints={lastResults.branchingPoints}
            pathTaken={lastResults.branchPath.path || []}
            pathScore={lastResults.branchPath.score}
            pathQuality={lastResults.branchPath.quality}
            onReplay={handleTryAgain}
          />
        </motion.div>
      )}

      {/* Key Moment */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="mb-8"
      >
        <Card>
          <Card.Header>
            <div className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-purple-400" />
              <Card.Title>Pivotal Moment</Card.Title>
            </div>
          </Card.Header>
          <Card.Content>
            {analysis.keyMoment ? (
              <div className="space-y-3">
                {analysis.keyMoment.timestamp && (
                  <p className="text-sm text-muted-foreground">{analysis.keyMoment.timestamp}</p>
                )}
                <p className="text-secondary-foreground">{analysis.keyMoment.description}</p>
                {analysis.keyMoment.impact && (
                  <p className="text-sm text-muted-foreground">
                    <span className="text-purple-400 font-medium">Impact:</span> {analysis.keyMoment.impact}
                  </p>
                )}
                {analysis.keyMoment.betterApproach && (
                  <div className="mt-3 p-3 bg-purple-500/10 rounded-lg border border-purple-500/20">
                    <span className="text-xs font-medium text-purple-700 dark:text-purple-300">Better approach:</span>
                    <p className="text-sm text-purple-900 dark:text-purple-100 mt-1">{analysis.keyMoment.betterApproach}</p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                The pivotal-moment breakdown wasn&apos;t captured for this session. Run this scenario again to get a fresh
                pivotal-moment and better-approach coaching for the call.
              </p>
            )}
          </Card.Content>
        </Card>
      </motion.div>

      {/* Next Steps */}
      {analysis.nextSteps && analysis.nextSteps.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mb-8"
        >
          <Card>
            <Card.Header>
              <div className="flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-blue-400" />
                <Card.Title>Next Steps</Card.Title>
              </div>
            </Card.Header>
            <Card.Content>
              <ul className="space-y-2">
                {analysis.nextSteps.map((step, index) => (
                  <li key={index} className="flex items-start gap-3 text-secondary-foreground">
                    <span className="w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-sm flex-shrink-0">
                      {index + 1}
                    </span>
                    {step}
                  </li>
                ))}
              </ul>
            </Card.Content>
          </Card>
        </motion.div>
      )}

      {/* Quick Actions Bar */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.65 }}
        className="flex flex-wrap gap-3 justify-center mb-6"
      >
        {lastResults.sessionId && (
          <Link
            to={`/replay/${lastResults.sessionId}`}
            onClick={() => trackEvent('results_next_step', { detail: 'open_replay' })}
            className="inline-flex items-center gap-2 px-4 py-2 bg-muted hover:bg-muted text-foreground rounded-lg transition-colors text-sm"
          >
            <Play className="w-4 h-4" />
            Replay Call
          </Link>
        )}
        <Link
          to={`/analysis/${scenario.id}`}
          className="inline-flex items-center gap-2 px-4 py-2 bg-muted hover:bg-muted text-foreground rounded-lg transition-colors text-sm"
        >
          <BarChart2 className="w-4 h-4" />
          Compare with Previous
        </Link>
      </motion.div>

      {/* Action Buttons */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="flex flex-col sm:flex-row gap-4 justify-center items-center"
      >
        {isModuleScenario ? (
          <>
            <Button
              variant="secondary"
              size="lg"
              onClick={handleBackToModule}
            >
              Back to Module
            </Button>
            <Button
              size="lg"
              icon={RotateCcw}
              onClick={handleNewScenario}
            >
              Next Scenario
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="secondary"
              size="lg"
              onClick={handleNewScenario}
            >
              Try Different Scenario
            </Button>
            <PracticeAgainButton
              scenarioId={scenario.id}
              scenarioName={scenario.name}
              lastScore={analysis.overallScore}
              onNavigate={() => { trackEvent('results_next_step', { detail: 'same_scenario' }); clearSession(); }}
            />
          </>
        )}
      </motion.div>
    </div>
  );
}

function getScoreLabel(score) {
  if (score >= 90) return 'Excellent!';
  if (score >= 80) return 'Great Job!';
  if (score >= 70) return 'Good Effort';
  if (score >= 60) return 'Room for Improvement';
  return 'Keep Practicing';
}

function formatCategoryName(key) {
  const names = {
    empathyRapport: 'Empathy & Rapport',
    bookingConversion: 'Booking & Conversion',
    serviceKnowledge: 'Service Knowledge',
    valueAndObjections: 'Value & Objections',
    professionalism: 'Professionalism',
    // Legacy names for backward compatibility
    problemResolution: 'Problem Resolution',
    productKnowledge: 'Product Knowledge',
    scenarioSpecific: 'Scenario Performance',
    communication: 'Communication'
  };
  return names[key] || key.replace(/([A-Z])/g, ' $1').trim();
}

export default Results;
